import { Link } from "react-router-dom";
import QuantityControl from "../helper/QuantityControl";
import React, { useEffect, useState, useCallback } from "react";
import { apiRequest, getProductDetails } from "../apiService/allapi";
import { successAlert, errorAlert } from "../apiService/sweetAlert";
import { dispatchCartUpdate } from "../helper/cartUtils";
import { getGuestCart, removeFromGuestCart, isLoggedIn, computeGuestCartTotal } from "../helper/cartUtils";

const CartSection = () => {
  const [cartItems, setCartItems] = useState([]);
  const [productDetailsCache, setProductDetailsCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [shippingCharge, setShippingCharge] = useState(0);
  const [isGuestCart, setIsGuestCart] = useState(false);
  const [imageLoading, setImageLoading] = useState({});

  const fetchMissingProductDetails = useCallback(async (items) => {
    const partialItems = items.filter(
      item => !item.imG1 || !item.productName || !item.mrp || !item.stock
    );
    if (partialItems.length === 0) return;

    try {
      const fetchPromises = partialItems.map(async (item) => {
        if (productDetailsCache[item.pid]) return null; // Cached
        try {
          const res = await getProductDetails(item.pid);
          if (res?.data?.data?.length > 0) {
            const details = res.data.data[0]; // First product
            return { pid: item.pid, details };
          }
        } catch (err) {
          console.error(`Failed to fetch product ${item.pid}:`, err);
        }
        return null;
      });

      const results = await Promise.all(fetchPromises);
      const newCache = { ...productDetailsCache };
      const updatedItems = [...items];

      results.forEach(result => {
        if (result) {
          newCache[result.pid] = result.details;
          // Merge into updatedItems
          const idx = updatedItems.findIndex(i => i.pid === result.pid);
          if (idx > -1) {
            updatedItems[idx] = {
              ...updatedItems[idx],
              imG1: result.details.imG1URL || result.details.imgurl || result.details.imG1,
              productName: result.details.productName,
              mrp: result.details.mrp,
              stock: result.details.stock || 999 // Add stock
            };
          }
        }
      });

      setProductDetailsCache(newCache);
      setCartItems(updatedItems);
    } catch (error) {
      console.error("Batch fetch error:", error);
    }
  }, [productDetailsCache]);

  const getCartItems = async () => {
    try {
      setLoading(true);
      
      if (isLoggedIn()) {
        const token = localStorage.getItem("token");
        const res = await apiRequest("/Cart/list", "GET", null, {
          Authorization: `Bearer ${token}`,
        });
        if (res?.data?.status === true) {
          const items = res.data.data.data || [];
          setCartItems(items);
          setIsGuestCart(false);
          // Fetch missing after set
          setTimeout(() => fetchMissingProductDetails(items), 100);
        }
      } else {
        const guestCart = getGuestCart();
        setCartItems(guestCart);
        setIsGuestCart(true);
        fetchMissingProductDetails(guestCart);
      }
    } catch (error) {
      console.log("Cart Error:", error);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCartItems();
  }, []); // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleCartUpdate = () => {
      getCartItems();
    };
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  // Hybrid remove
  const removeCartItem = async (pid) => {
    try {
      if (isLoggedIn()) {
        const token = localStorage.getItem("token");
        const res = await apiRequest("/Cart/deleteFromCart", "POST", { pid }, {
          Authorization: `Bearer ${token}`,
        });
        if (res?.data?.status === true) {
          dispatchCartUpdate();
          getCartItems();
          successAlert(res.data.message);
        }
      } else {
        const newCart = removeFromGuestCart(pid);
        setCartItems(newCart);
        dispatchCartUpdate();
        successAlert("Removed from cart");
      }
    } catch (error) {
      console.log("Remove Error:", error);
      errorAlert("Failed to remove item");
    }
  };

  // Hybrid total
  const getCartTotal = async () => {
    try {
      if (isLoggedIn()) {
        const token = localStorage.getItem("token");
        const res = await apiRequest("/Cart/total", "GET", null, {
          Authorization: `Bearer ${token}`,
        });
        if (res?.data?.status === true) {
          setTotalPrice(res.data.data.totalNetAmount || 0);
          setShippingCharge(res.data.data.totalShippingCharges || 0);
        }
      } else {
        setTotalPrice(computeGuestCartTotal(cartItems));
        setShippingCharge(0);
      }
    } catch (error) {
      console.log("Total Error:", error);
      if (!isLoggedIn()) {
        setTotalPrice(computeGuestCartTotal(cartItems));
        setShippingCharge(0);
      }
    }
  };

  useEffect(() => {
    getCartTotal();
  }, [cartItems, isGuestCart]);

  const handleCheckoutClick = () => {
    if (!isLoggedIn()) {
      errorAlert("Please login to proceed to checkout");
      return;
    }
  };

  const handleImageLoad = (pid, loaded) => {
    setImageLoading(prev => ({ ...prev, [pid]: !loaded }));
  };

  const getImageSrc = (item) => {
    const img = item.imG1 || productDetailsCache[item.pid]?.imG1;
    return img ? `${process.env.REACT_APP_IMAGE_BASE_URL}${img}` : '/assets/images/thumbs/no-image.png';
  };

  const getProductName = (item) => item.productName || productDetailsCache[item.pid]?.productName || 'Unknown Product';

  const getmrp = (item) => parseFloat(item.mrp || productDetailsCache[item.pid]?.mrp || 0);

  return (
    <section className="cart py-80">
      <div className="container container-lg">
        <div className="row gy-4">
          {cartItems.length === 0 && !loading && (
            <div className="text-center py-80">
              <h4>Your Cart is Empty 🛒</h4>
            </div>
          )}
          <div className="col-xl-9 col-lg-8">
            <div className="cart-table border border-gray-100 rounded-8 px-40 py-48">
              <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                <table className="table style-three">
                  {cartItems.length > 0 && (
                    <thead>
                      <tr>
                        <th className="h6 mb-0 text-lg fw-bold">Delete</th>
                        <th className="h6 mb-0 text-lg fw-bold">
                          Product Name
                        </th>
                        <th className="h6 mb-0 text-lg fw-bold">Price</th>
                        <th className="h6 mb-0 text-lg fw-bold">Quantity</th>
                        <th className="h6 mb-0 text-lg fw-bold">Subtotal</th>
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="text-center py-40">
                          <div className="spinner-border text-main-600" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      cartItems.map((item) => {
                        const price = getmrp(item);
                        return (
                          <tr key={item.pid}>
                            <td>
                              <button
                                onClick={() => removeCartItem(item.pid)}
                                className="remove-tr-btn flex-align gap-12 hover-text-danger-600"
                              >
                                <i className="ph ph-x-circle text-2xl d-flex" />
                                Remove
                              </button>
                            </td>

                            <td>
                              <div className="table-product d-flex align-items-center gap-24">
                                <div className="table-product__thumb border border-gray-100 rounded-8 flex-center position-relative">
                                  {imageLoading[item.pid] ? (
                                    <div className="spinner-border spinner-border-sm position-absolute" />
                                  ) : null}
                                  <img
                                    src={getImageSrc(item)}
                                    alt={getProductName(item)}
                                    onLoad={() => handleImageLoad(item.pid, true)}
                                    onError={() => handleImageLoad(item.pid, false)}
                                    className="object-fit-cover"
                                  />
                                </div>

                                <div className="table-product__content text-start">
                                  <h6 className="title text-lg fw-semibold mb-8">
                                    {getProductName(item)}
                                  </h6>
                                  {item.stock !== undefined && item.stock < 10 && (
                                    <span className="text-sm text-warning-600">
                                      Low stock: {item.stock} left
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td>
                              <span className="text-lg h6 mb-0 fw-semibold">
                                ₹{price.toFixed(2)}
                              </span>
                            </td>

                            <td>
                              <QuantityControl
                                initialQuantity={parseInt(item.qty)}
                                itemId={item.pid}
                                maxStock={item.stock || productDetailsCache[item.pid]?.stock || Infinity}
                                refreshCart={getCartItems}
                              />
                            </td>
                            <td>
                              <span className="text-lg h6 mb-0 fw-semibold">
                                ₹{(price * item.qty).toFixed(2)}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex-between flex-wrap gap-16 mt-16">
                {/* Update Cart button optional */}
              </div>
            </div>
          </div>
          {cartItems.length > 0 && (
            <div className="col-xl-3 col-lg-4">
              <div className="cart-sidebar border border-gray-100 rounded-8 px-24 py-40">
                <h6 className="text-xl mb-32">Cart Totals</h6>

                <div className="bg-color-three rounded-8 p-24">
                  <div className="mb-32 flex-between gap-8">
                    <span className="text-gray-900 font-heading-two">
                      Estimated Delivery
                    </span>
                    <span className="text-gray-900 fw-semibold">
                      ₹{shippingCharge}
                    </span>
                  </div>

                  <div className="mb-0 flex-between gap-8">
                    <span className="text-gray-900 font-heading-two">
                      Estimated Tax
                    </span>
                    <span className="text-gray-900 fw-semibold">₹0</span>
                  </div>
                </div>

                <div className="bg-color-three rounded-8 p-24 mt-24">
                  <div className="flex-between gap-8">
                    <span className="text-gray-900 text-xl fw-semibold">
                      Total
                    </span>
                    <span className="text-gray-900 text-xl fw-semibold">
                      ₹{totalPrice + shippingCharge}
                    </span>
                  </div>
                </div>

         <button
  onClick={() => {
    window.location.href = "/checkout";
  }}
  className="btn btn-main mt-40 py-18 w-100 rounded-8"
>
  Proceed to Checkout
</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CartSection;

